/**
 * Blog post manifest. Posts live as `.md` files in `posts/` and are
 * imported here as raw strings (Vite `?raw` query). When you add a new
 * post:
 *   1. Drop the .md file into `posts/<slug>.md`
 *   2. Add a `?raw` import + entry to POSTS below
 * That's it for an English post — both the sitemap (scripts/gen-sitemap.mjs)
 * and the prerendered meta (scripts/prerender.mjs) auto-discover it from
 * POSTS, so there's no sitemap.xml or route-meta.json edit to forget.
 *
 * Each post is rendered by `<BlogPost>` via `react-markdown`. The list
 * page `<BlogIndex>` uses the metadata here (title, date, excerpt) to
 * render the index without parsing the markdown content.
 *
 * Order in POSTS is newest-first (display order on /blog).
 */

import theCardGraph from './posts/the-card-graph.md?raw';
import whyYourPrdsDrift from './posts/why-your-prds-drift.md?raw';
import decisionsWithClaudeCode from './posts/decisions-with-claude-code.md?raw';
import decisionLogsInTheAiEra from './posts/decision-logs-in-the-ai-era.md?raw';
import prdTemplatesThatDontDrift from './posts/prd-templates-that-dont-drift.md?raw';
import stopReExplainingContext from './posts/stop-re-explaining-context.md?raw';
import whyWeBuiltDraftlize from './posts/why-we-built-draftlize.md?raw';
import chatToArtifactLoop from './posts/chat-to-artifact-loop.md?raw';
import mcpIntegrationClaudeCode from './posts/mcp-integration-claude-code.md?raw';
import specDriftDemo from './posts/spec-drift-demo.md?raw';
import cardGraphsVsSecondBrains from './posts/card-graphs-vs-second-brains.md?raw';
import contextEngineering from './posts/context-engineering.md?raw';
import llmContextWindow from './posts/llm-context-window.md?raw';
import howToWriteAPrd from './posts/how-to-write-a-prd.md?raw';
import whatIsAPrd from './posts/what-is-a-prd.md?raw';
import whatDoProductManagersDo from './posts/what-do-product-managers-do.md?raw';
import obsidianVsNotion from './posts/obsidian-vs-notion.md?raw';
import customerFeedbackAnalysis from './posts/customer-feedback-analysis.md?raw';
import competitorAnalysisFramework from './posts/competitor-analysis-framework.md?raw';

// zh translations (PR: blog i18n). Only a high-value subset is translated;
// posts without a `zh` field fall back to English on /zh/blog. Add a new
// translation by dropping `posts/<slug>.zh.md` + a `?raw` import + filling
// the `zh` field on the matching POSTS entry + a /zh/blog/<slug> entry in
// scripts/route-meta.json (that one entry feeds BOTH the prerendered meta
// and the generated sitemap).
import theCardGraphZh from './posts/the-card-graph.zh.md?raw';
import whyYourPrdsDriftZh from './posts/why-your-prds-drift.zh.md?raw';
import stopReExplainingContextZh from './posts/stop-re-explaining-context.zh.md?raw';
import contextEngineeringZh from './posts/context-engineering.zh.md?raw';
import llmContextWindowZh from './posts/llm-context-window.zh.md?raw';
import howToWriteAPrdZh from './posts/how-to-write-a-prd.zh.md?raw';
import whatIsAPrdZh from './posts/what-is-a-prd.zh.md?raw';
import whatDoProductManagersDoZh from './posts/what-do-product-managers-do.zh.md?raw';
import obsidianVsNotionZh from './posts/obsidian-vs-notion.zh.md?raw';
import customerFeedbackAnalysisZh from './posts/customer-feedback-analysis.zh.md?raw';
import competitorAnalysisFrameworkZh from './posts/competitor-analysis-framework.zh.md?raw';

export interface BlogPostMeta {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  /** Estimated read time as a short label (e.g. "6 min read"). */
  readTime: string;
  /** Raw markdown body — fed to react-markdown by <BlogPost>. */
  content: string;
  /** Optional zh translation. When present, /zh/blog renders these; when
   *  absent, the post falls back to English (no /zh/ URL is advertised
   *  for it in the sitemap). */
  zh?: { title: string; excerpt: string; content: string };
}

/** Pick the locale-appropriate {title, excerpt, content} for a post.
 *  Falls back to English when the post has no zh translation. */
export function localized(
  p: BlogPostMeta,
  locale: 'en' | 'zh',
): { title: string; excerpt: string; content: string } {
  if (locale === 'zh' && p.zh) return p.zh;
  return { title: p.title, excerpt: p.excerpt, content: p.content };
}

export const POSTS: BlogPostMeta[] = [
  {
    slug: 'obsidian-vs-notion',
    title: 'Obsidian vs Notion: An Honest Comparison and How to Choose',
    date: '2026-06-29',
    excerpt:
      "Obsidian and Notion sit at opposite ends of a real design spectrum: local-first Markdown files vs cloud-based blocks, personal PKM vs team collaboration. A no-spin, third-party comparison across seven dimensions — plus a clear framework for which one you should actually pick.",
    readTime: '9 min read',
    content: obsidianVsNotion,
    zh: {
      title: 'Obsidian vs Notion：一篇诚实的对比，以及你该怎么选',
      excerpt:
        'Obsidian 和 Notion 站在一条真实设计光谱的两端：本地优先的 Markdown 文件 vs 云端的块结构、个人知识管理 vs 团队协作。一篇不站台的第三方对比，从七个维度讲清取舍，并给你一个「到底该选哪个」的判断框架。',
      content: obsidianVsNotionZh,
    },
  },
  {
    slug: 'what-do-product-managers-do',
    title: 'What do product managers do? A clear look at the role',
    date: '2026-06-29',
    excerpt:
      "Product managers own what gets built and why — not the building itself. A clear guide to the core responsibilities, the real day-to-day, the skills that matter, how the role differs from project manager and product owner, and the invisible work nobody warns you about.",
    readTime: '9 min read',
    content: whatDoProductManagersDo,
    zh: {
      title: '产品经理到底做什么？把这个角色讲清楚',
      excerpt:
        '产品经理负责「做什么」和「为什么做」，而不是做本身。一篇讲清核心职责、日常真实样子、需要的能力、它和项目经理/产品负责人的区别，以及那一块没人会提醒你的隐形工作的科普文。',
      content: whatDoProductManagersDoZh,
    },
  },
  {
    slug: 'customer-feedback-analysis',
    title: 'Customer feedback analysis: turning the mess into product decisions',
    date: '2026-06-29',
    excerpt:
      "A practical guide to customer feedback analysis — collect across channels, tag and quantify by frequency, impact, and segment, surface the real themes, and turn them into decisions. Plus the part most guides skip: why the line from insight to decision to shipped product keeps breaking.",
    readTime: '8 min read',
    content: customerFeedbackAnalysis,
    zh: {
      title: '客户反馈分析：怎么把一团乱麻变成产品决策',
      excerpt:
        '一篇客户反馈分析的实操指南——怎么跨渠道收集、按频率/影响/客群打标签和量化、浮出真正的主题、再把它们变成决策。还有大多数指南都跳过的那部分：为什么从洞察到决策到上线的那根线会反复断掉。',
      content: customerFeedbackAnalysisZh,
    },
  },
  {
    slug: 'competitor-analysis-framework',
    title: 'Competitor analysis framework: 5 frameworks and when to use each',
    date: '2026-06-29',
    excerpt:
      "SWOT, Porter's Five Forces, feature matrix, perceptual map, competitive matrix — what each is good at, when to use it, and a worked example for each. Plus the problem none of them solve: a competitor analysis is right the day you finish it and wrong the moment a rival reprices.",
    readTime: '9 min read',
    content: competitorAnalysisFramework,
    zh: {
      title: '竞品分析框架：5 个实用框架，以及各自该在什么时候用',
      excerpt:
        'SWOT、波特五力、功能矩阵、感知图、竞品对比矩阵——每个擅长什么、什么时候用，各配一个走完一遍的小例子。再加上它们都解决不了的那个问题：竞品分析做完那天是对的，对手一改价就错了。',
      content: competitorAnalysisFrameworkZh,
    },
  },
  {
    slug: 'what-is-a-prd',
    title: 'What is a PRD? A plain guide to the product requirements document',
    date: '2026-06-29',
    excerpt:
      "A PRD (product requirements document) describes what you're building and why, so the whole team can decide without re-asking you. What it answers, the parts it's made of, who writes it, how it differs from a spec, brief, or BRD — and the failure mode nobody warns you about.",
    readTime: '8 min read',
    content: whatIsAPrd,
    zh: {
      title: '什么是 PRD？一份关于产品需求文档的通俗指南',
      excerpt:
        'PRD（产品需求文档）讲清楚你要做什么、为什么做，好让整个团队不必反复来问你。本文拆解它回答哪些问题、由哪几部分组成、谁来写、和 spec / brief / BRD 有何区别，以及那个没人提醒你的真正失败方式：漂移。',
      content: whatIsAPrdZh,
    },
  },
  {
    slug: 'context-engineering',
    title: 'Context engineering: the skill that replaced prompt engineering',
    date: '2026-06-28',
    excerpt:
      "Prompt engineering optimized the wording of one call. But agents run for many steps, and what decides their quality is what they're looking at when they read each instruction. That's context engineering — and why bigger context windows don't solve it.",
    readTime: '7 min read',
    content: contextEngineering,
    zh: {
      title: '上下文工程：取代提示词工程的那门手艺',
      excerpt:
        '提示词工程优化的是单次调用的措辞。但 agent 会跑很多步，决定它质量的，是它在读每条指令时眼前是什么。那就是上下文工程——以及为什么更大的窗口解决不了它。',
      content: contextEngineeringZh,
    },
  },
  {
    slug: 'llm-context-window',
    title: "LLM context window, explained: why a bigger one won't make your AI remember",
    date: '2026-06-28',
    excerpt:
      "A bigger context window raises the ceiling on what you can paste — but it isn't memory, and past a point it stops helping. Why a full window still forgets, and what actually keeps an AI consistent on long projects.",
    readTime: '7 min read',
    content: llmContextWindow,
    zh: {
      title: '一文讲透 LLM context window：为什么窗口更大，AI 也不会真的「记住」',
      excerpt:
        '更大的 context window 只是抬高了你能贴多少的天花板——它不是记忆，过了某个点就不再有用。为什么塞满了照样会忘，以及做长项目时什么才能真正让 AI 保持前后一致。',
      content: llmContextWindowZh,
    },
  },
  {
    slug: 'how-to-write-a-prd',
    title: 'How to Write a PRD (That Survives Contact With the Team)',
    date: '2026-06-28',
    excerpt:
      "A practical guide to writing a PRD — the eight sections that earn their place, and the part everyone skips: why PRDs fail by drifting, not by being badly written, and what fixes that.",
    readTime: '7 min read',
    content: howToWriteAPrd,
    zh: {
      title: '怎么写一份好的 PRD（一份写完之后还活着的产品需求文档）',
      excerpt:
        'PRD 怎么写——值得留下的 8 个章节，以及几乎所有人都跳过的那部分：PRD 不是写得差才失败，是写完之后漂移死的。',
      content: howToWriteAPrdZh,
    },
  },
  {
    slug: 'the-card-graph',
    title: 'The Card Graph: A new substrate for AI-assisted product thinking',
    date: '2026-05-28',
    excerpt:
      "Chat history is the wrong shape for product thinking. Card graphs are a different substrate — one whose primary user is an AI agent collaborating with a human, not a human reading the document later.",
    readTime: '6 min read',
    content: theCardGraph,
    zh: {
      title: '卡片图：AI 辅助产品思考的新 substrate',
      excerpt:
        '聊天历史对产品思考来说是错的形状。卡片图是一种不同的 substrate——它的首要使用者是和人协作的 AI agent，而不是事后读文档的人。',
      content: theCardGraphZh,
    },
  },
  {
    slug: 'why-your-prds-drift',
    title: 'Why your PRDs drift after every sprint (and how to stop it)',
    date: '2026-05-28',
    excerpt:
      "Spec drift isn't a discipline problem. It's a substrate problem. Here's how dependency tracking solves it the same way invalidated build artifacts get solved — automatically.",
    readTime: '5 min read',
    content: whyYourPrdsDrift,
    zh: {
      title: '为什么你的 PRD 每个 sprint 之后都会漂移（以及怎么止住）',
      excerpt:
        'Spec drift 不是纪律问题，是 substrate 问题。看依赖追踪怎么用工程师解决缓存失效的同一套办法——自动地——解决它。',
      content: whyYourPrdsDriftZh,
    },
  },
  {
    slug: 'decisions-with-claude-code',
    title: 'How I run product decisions with Claude Code + Draftlize',
    date: '2026-05-28',
    excerpt:
      "A concrete workflow walkthrough. Brainstorm in Draftlize, implement in Claude Code via /draftlize:cite, mark built, watch for drift_detected when the spec moves.",
    readTime: '6 min read',
    content: decisionsWithClaudeCode,
  },
  {
    slug: 'decision-logs-in-the-ai-era',
    title: 'Decision logs in the AI era: what changes when AI can read them',
    date: '2026-05-28',
    excerpt:
      "ADRs have been around since 2011 — and have always atrophied. What changes when the audience isn't only humans? The AI agent becomes the discipline-enforcing reader.",
    readTime: '5 min read',
    content: decisionLogsInTheAiEra,
  },
  {
    slug: 'prd-templates-that-dont-drift',
    title: '5 PRD templates that don\'t drift',
    date: '2026-05-28',
    excerpt:
      "Five PRD templates built on cards instead of prose. The structure makes drift visible the moment dependencies move. Each ships as a starter pack in Draftlize.",
    readTime: '5 min read',
    content: prdTemplatesThatDontDrift,
  },
  {
    slug: 'stop-re-explaining-context',
    title: 'Stop re-explaining context to AI: A PM\'s guide to persistent agent memory',
    date: '2026-05-28',
    excerpt:
      "Why ChatGPT Memory / Claude Projects don't fully fix the re-explanation problem, and what actually does. Plus a practical 4-step switch guide.",
    readTime: '5 min read',
    content: stopReExplainingContext,
    zh: {
      title: '别再给 AI 反复解释 context：PM 的持久化 agent 记忆指南',
      excerpt:
        '为什么 ChatGPT Memory / Claude Projects 没能真正解决反复解释的问题，以及什么才能。外加一份实操的四步切换指南。',
      content: stopReExplainingContextZh,
    },
  },
  {
    slug: 'why-we-built-draftlize',
    title: 'Why we built Draftlize (and what dogfooding taught us)',
    date: '2026-05-28',
    excerpt:
      "The personal version. The bet behind the substrate, the secondary bets we got wrong (free tier, voice input, audience), and what dogfood taught us about who actually loves it.",
    readTime: '6 min read',
    content: whyWeBuiltDraftlize,
  },
  {
    slug: 'chat-to-artifact-loop',
    title: 'From chat to artifact: The Draftlize loop in 5 minutes',
    date: '2026-05-28',
    excerpt:
      "The conceptual heart of Draftlize is a loop: chat with the agent, agent writes cards, cards become artifacts, artifacts feed future chats. 5 minutes to understand.",
    readTime: '4 min read',
    content: chatToArtifactLoop,
  },
  {
    slug: 'mcp-integration-claude-code',
    title: 'The MCP integration: Why Draftlize is first-class context for Claude Code',
    date: '2026-05-28',
    excerpt:
      "Why Draftlize shipped MCP server support on day one of the protocol. What the integration enables. 60-second setup with Claude Code, Cursor, or Claude Desktop.",
    readTime: '5 min read',
    content: mcpIntegrationClaudeCode,
  },
  {
    slug: 'spec-drift-demo',
    title: 'What happens to your PRD when you change one decision: a concrete demo',
    date: '2026-05-28',
    excerpt:
      "Real cards, real edits, real cascade. A walkthrough of what Draftlize does when you change a single decision in a 47-card project — and the 12 dependents that flag automatically.",
    readTime: '4 min read',
    content: specDriftDemo,
  },
  {
    slug: 'card-graphs-vs-second-brains',
    title: 'Card graphs vs second brains: why context for AI is different from notes for humans',
    date: '2026-05-28',
    excerpt:
      "Obsidian / Roam / Notion are wonderful for human knowledge work. They're the wrong shape for AI context. The mismatch is structural — and explains why a new tooling category is emerging.",
    readTime: '5 min read',
    content: cardGraphsVsSecondBrains,
  },
];

export function postBySlug(slug: string): BlogPostMeta | undefined {
  return POSTS.find((p) => p.slug === slug);
}
